/*
 * Copyright 2020 Adobe. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export function useControlledState<T, C = T>(
  value: Exclude<T, undefined>,
  defaultValue: Exclude<T, undefined> | undefined,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange?: (v: C, ...args: any[]) => void,
): [T, (value: T) => void];
export function useControlledState<T, C = T>(
  value: Exclude<T, undefined> | undefined,
  defaultValue: Exclude<T, undefined>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange?: (v: C, ...args: any[]) => void,
): [T, (value: T) => void];
export function useControlledState<T, C = T>(
  value: T,
  defaultValue: T,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange?: (v: C, ...args: any[]) => void,
): [T, (value: T) => void] {
  const [stateValue, setStateValue] = useState(value || defaultValue);

  const isControlledRef = useRef(value !== undefined);
  const isControlled = value !== undefined;

  useEffect(() => {
    const wasControlled = isControlledRef.current;
    if (wasControlled !== isControlled) {
      // eslint-disable-next-line no-console
      console.warn(
        `WARN: A component changed from ${wasControlled ? 'controlled' : 'uncontrolled'} to ${
          isControlled ? 'controlled' : 'uncontrolled'
        }.`,
      );
    }
    isControlledRef.current = isControlled;
  }, [isControlled]);

  let currentValue = isControlled ? value : stateValue;
  // eslint-disable-next-line no-restricted-syntax
  const setValue = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (val: any, ...args: any[]) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const onChangeCaller = (v: any, ...onChangeArgs: any[]) => {
        if (onChange) {
          if (!Object.is(currentValue, v)) {
            onChange(v, ...onChangeArgs);
          }
        }
        if (!isControlled) {
          // If uncontrolled, mutate the currentValue local variable so that
          // calling setState multiple times with the same value only emits onChange once.
          // We do not use a ref for this because we specifically _do_ want the value to
          // reset every render, and assigning to a ref in render breaks aborted suspended renders.
          // eslint-disable-next-line react-hooks/exhaustive-deps
          currentValue = v;
        }
      };

      if (typeof val === 'function') {
        // eslint-disable-next-line no-console
        console.warn(
          'We can not support a function callback. See Github Issues for details https://github.com/adobe/react-spectrum/issues/2320',
        );
        // this supports functional updates https://reactjs.org/docs/hooks-reference.html#functional-updates
        // when someone using useControlledState calls setControlledState(myFunc)
        // this will call our useState setState with a function as well which invokes myFunc and calls onChange with the value from myFunc
        // if we're in an uncontrolled state, then we also return the value of myFunc which to setState looks as though it was just called with myFunc from the beginning
        // otherwise we just return the controlled value, which won't cause a rerender because React knows to bail out when the value is the same
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const updateFunction = (oldValue: any, ...functionArgs: any[]) => {
          const interceptedValue = val(isControlled ? currentValue : oldValue, ...functionArgs);
          onChangeCaller(interceptedValue, ...args);
          if (!isControlled) {
            return interceptedValue;
          }
          return oldValue;
        };
        setStateValue(updateFunction);
      } else {
        if (!isControlled) {
          setStateValue(val);
        }
        onChangeCaller(val, ...args);
      }
    },
    [isControlled, currentValue, onChange],
  );

  return [currentValue, setValue];
}
